import React, { useEffect, useState } from 'react'
import omit from 'lodash/omit'
import { Button, Form, FormCheck, FormGroup } from 'react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import { Field, FieldArray, Formik, yupToFormErrors } from 'formik'
import { useServiceContext } from '~/root'
import { AuthorizationValidationSchema } from './WFPhaseValidation'
import { transformFromServer, transformToServer } from '~/pages/settings/workflow-templates/DataService'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { FsiType } from '~/constants/fsi'
import { WF_STEP_TYPE } from '~/types/workflow'
import UserGroupSteps from '~/pages/settings/workflow-templates/UserGroupSteps'
import { toast } from '~/services/toast'
import { getFileCRApprovers, updateFileCRApprovers } from '~/services/api/files'
import _ from 'lodash'

function removeMergedObj(obj: Record<string, any>, key: string): Record<string, any> {
  const steps = obj[key]?.steps?.reduce((acc: any[], step: any) => {
    acc.push(omit(step, 'merged'))
    return acc
  }, [])
  return {
    ...obj,
    [key]: { steps, type: obj[key]?.type }
  }
}
interface AuthorizationPhaseModalProps {
  fsi: any
  onClose?: () => void
}

function AuthorizationPhaseModal({ fsi, onClose }: AuthorizationPhaseModalProps) {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState<boolean>(true)
  const [formData, setFormData] = useState<Record<string, any>>({})
  const authorizationValidationSchema = AuthorizationValidationSchema(translate)

  useEffect(() => {
    if (fsi?.id) {
      getFileCRApprovers(fsi.id).then((data: any) => {
        setFormData(transformFromServer(data, undefined))
      })
    }
  }, [fsi])

  const doClose = (): void => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  function doSave(values: Record<string, any>): void {
    const cloneVals = cloneDeep(values)
    const saveRecursivelyVar = cloneVals.saveRecursivelyVar
    delete cloneVals.saveRecursivelyVar
    const finalData = removeMergedObj(transformToServer(cloneVals), 'initialApprovers')
    updateFileCRApprovers(
      fsi.id,
      { initialApprovers: finalData.initialApprovers },
      false,
      saveRecursivelyVar,
      true
    ).then(
      (data: any) => {
        doClose()
      },
      (data: any) => {
        toast.error(data?.data?.message ? data.data.message : translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
      }
    )
  }

  return (
    <Modal show={isOpen} size="lg" title={translate('BROWSER_ACTION_CR_APPROVERS')} handleClose={doClose}>
      <Formik
        innerRef={(el) => {
          el ? (context.store.formRef = el) : null
        }}
        initialValues={{
          ...cloneDeep(formData)
        }}
        enableReinitialize
        validate={(values: Record<string, any>) => {
          return authorizationValidationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => {
                return yupToFormErrors(err)
              }
            )
        }}
        onSubmit={(values: Record<string, any>) => {
          doSave(values)
        }}
      >
        {({
          values,
          handleSubmit,
          setFieldValue
        }: {
          values: Record<string, any>
          handleSubmit: () => void
          setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
        }) => (
          <>
            <Modal.Body>
              <div className="form-group md-form-group w100p">
                <label>{fsi?.type === FsiType.FOLDER ? translate('FSI_TYPE_FOLDER') : translate('DOCUMENT')}</label>
                {fsi?.name ? <p className="whitespaceprewrap fs12">{fsi.name}</p> : null}
              </div>
              {values?.workflowTemplate?.id && (
                <div className="form-group md-form-group w100p">
                  <label>{translate('WF_TEMPLATE_LABEL')}</label>
                  <p>{values.workflowTemplate?.name || '-'}</p>
                </div>
              )}
              <FormGroup>
                <Form.Label>{translate('BROWSER_FILE_SETTINGS_CR_APPROVERS_AUTHORIZATION_TYPE_HEADLINE')}</Form.Label>
                <FormCheck
                  name="initialApprovers.type"
                  value={WF_STEP_TYPE.SETUP_WITH_WORKFLOW}
                  checked={values?.initialApprovers?.type === WF_STEP_TYPE.SETUP_WITH_WORKFLOW}
                  type="radio"
                  onChange={() => setFieldValue('initialApprovers.type', WF_STEP_TYPE.SETUP_WITH_WORKFLOW)}
                  id={`42902a68-2e1d-42a7-9ff2-de92caf7fa1a-${WF_STEP_TYPE.SETUP_WITH_WORKFLOW}`}
                  disabled={values?.readOnly}
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_CR_APPROVERS_LABEL_SETUP_AUTHORIZERS_WITH_CR')}
                    </div>
                  }
                />

                <FormCheck
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_CR_APPROVERS_LABEL_USE_PREDEFINED')}
                    </div>
                  }
                  name="initialApprovers.type"
                  value={WF_STEP_TYPE.USE_PREDEFINED}
                  onChange={() => setFieldValue('initialApprovers.type', WF_STEP_TYPE.USE_PREDEFINED)}
                  checked={values?.initialApprovers?.type === WF_STEP_TYPE.USE_PREDEFINED}
                  type="radio"
                  id={`42902a68-2e1d-42a7-9ff2-de92caf7fa1a-${WF_STEP_TYPE.USE_PREDEFINED}`}
                  disabled={values?.readOnly}
                />

                <FormCheck
                  name="initialApprovers.type"
                  value={WF_STEP_TYPE.NOT_DEFINED}
                  onChange={() => setFieldValue('initialApprovers.type', WF_STEP_TYPE.NOT_DEFINED)}
                  checked={values?.initialApprovers?.type === WF_STEP_TYPE.NOT_DEFINED}
                  type="radio"
                  id={`42902a68-2e1d-42a7-9ff2-de92caf7fa1a-${WF_STEP_TYPE.NOT_DEFINED}`}
                  disabled={values?.readOnly}
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_CR_APPROVERS_LABEL_NOT_DEFINED')}
                    </div>
                  }
                />
              </FormGroup>

              {values?.initialApprovers?.type === WF_STEP_TYPE.USE_PREDEFINED ? (
                <FieldArray name="initialApprovers.steps">
                  {(arrayHelpers) => (
                    <UserGroupSteps
                      arrayHelpers={arrayHelpers}
                      values={values?.initialApprovers ?? []}
                      nofallTxt={'BROWSER_FILE_SETTINGS_CR_APPROVERS_INFO_LABEL'}
                      labelTxt={'BROWSER_FILE_SETTINGS_CR_APPROVERS_APPROVAL_SEQUENCE_HEADLINE'}
                      disabled={!!values?.readOnly}
                      isFromManageWFModals={true}
                    />
                  )}
                </FieldArray>
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              {!!values?.readOnly && (
                <Button className="btn btn-primary at_btnSaveApprovers" type="button" onClick={doClose}>
                  {translate('OK')}
                </Button>
              )}
              {!values?.readOnly && (
                <>
                  <Button className="btn btn-close" variant="link" type="button" onClick={doClose}>
                    {translate('CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn-primary at_btnSaveApprovers"
                    onClick={() => {
                      setFieldValue('saveRecursivelyVar', false)
                      handleSubmit()
                    }}
                  >
                    {translate('SAVE_BTN')}
                  </Button>
                </>
              )}
              {fsi?.type === context.constant.file.type.FOLDER && !values?.readOnly && (
                <Button
                  type="submit"
                  className="btn btn-primary at_btnSaveAclsRecursively"
                  onClick={() => {
                    setFieldValue('saveRecursivelyVar', true)
                    handleSubmit()
                  }}
                >
                  {translate('SAVE_FOR_ENCLOSED_ITEMS')}
                </Button>
              )}
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default AuthorizationPhaseModal
